import {
  Flex,
  ModalConfirmation,
  ModalConfirmationProps,
  notify,
  SectionMessage,
  Spacing,
  Text,
  useModal,
} from '@billy/design-system'
import { ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { UiSource } from '@features/reconciliation/enums'
import { useUnmatchBankTransaction } from '@features/reconciliation/hooks'
import { invalidateReconciliationQueries } from '@features/reconciliation/utils'

import { queryClient } from '../../../../../../../../config/queryClient'
import { NotificationKeys } from '../../../../../../../../enums/notificationKeys'
import { APIError } from '../../../../../../../../utils'
import { getErrorMessage } from '../../../../../../../../utils/getErrorMessage'
import { useSandboxModeContext } from '../../../../contexts'

interface UnmatchConfirmationModalProps extends ModalConfirmationProps {
  matchId: string
  onUnmatched?: () => void
}

export const UnmatchConfirmationModal = ({
  matchId,
  onUnmatched,
  id,
  ...props
}: UnmatchConfirmationModalProps): ReactElement => {
  const { t } = useTranslation()
  const { close } = useModal(id)
  const sandbox = useSandboxModeContext()

  const { unmatch, isProcessing } = useUnmatchBankTransaction({
    onSuccess: async () => {
      await invalidateReconciliationQueries(queryClient)
      close()
      onUnmatched?.()
    },
    onError: (error?: APIError) => {
      const message = getErrorMessage(error) || t('something_went_wrong')
      notify({ id: NotificationKeys.BankReconciliationMatchUnmatch, message, variant: 'error' })
    },
    uiSource: UiSource.AccountingTransactionView,
  })

  const handleOkClick = useCallback(() => {
    if (sandbox) {
      sandbox.unmatchBankLineInSandbox(matchId)
      close()
      onUnmatched?.()
      return
    }

    unmatch(matchId)
  }, [sandbox, matchId, close, onUnmatched, unmatch])

  const message = (
    <Flex flexDirection="column" gap={Spacing.S16}>
      <Text colorVariant="secondary">{t('bank_transactions.drawer.unmatch_modal.intro')}</Text>

      <SectionMessage variant="warning">{t('bank_transactions.drawer.unmatch_modal.warning')}</SectionMessage>
    </Flex>
  )

  return (
    <ModalConfirmation
      {...props}
      id={id}
      cancelLabel={t('cancel')}
      danger
      message={message}
      okLabel={t('bank_transactions.drawer.unmatch_modal.ok')}
      okLoading={isProcessing}
      onOk={handleOkClick}
      title={t('bank_transactions.drawer.unmatch_modal.title')}
    />
  )
}
